import React, { useState, useMemo, useEffect } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { useSelector } from 'react-redux';
import LHOverlay from '../../components/LHOverlay';
import LhButton from '../../components/LhButton';
import CrossPlatformDropdown from '../../components/CrossPlatformDropdown';
import { AdminColors } from '../Admin/constants/adminColors';
import FontFamily from '../../constants/fonts';

const ACTIVITY_TYPES = [
  { id: 'pulse_check', displayText: 'Pulse Check' },
  { id: 'routine_observation', displayText: 'Routine Observation' },
  { id: 'full_observation', displayText: 'Full Observation' },
];

const ACTIVITY_DESCRIPTIONS: Record<string, string> = {
  'Pulse Check':
    'One sitting: review metrics, record balanced feedback and topics discussed, and close out every open coaching item.',
  'Routine Observation': 'A shorter role-based walk-through using the routine question set.',
  'Full Observation': 'The complete role-based observation, feedback, and action-item workflow.',
};

interface BeginActivityOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  /** Resolves once the start-activity call finishes; the overlay stays open
   *  with a loading Proceed button until then and only closes on success
   *  (the caller is responsible for closing it, e.g. via visible/onDismiss). */
  onProceed: (data: { employeeId: string; employee: string; role: string; managerName: string; activityType: string }) => Promise<void>;
  /** When set (e.g. opened from an Employee Profile page), the Employee field is
   *  pre-filled and locked to this employee — only Role and Activity type stay editable. */
  presetEmployee?: { employeeId: string; employeeName: string; role?: string };
}

const BeginActivityOverlay: React.FC<BeginActivityOverlayProps> = ({ visible, onDismiss, onProceed, presetEmployee }) => {
  const { employees, roles } = useSelector((state: any) => state.appData);

  // Build employee options from Redux (same as ObservationBegin / ObservationFilters pattern)
  const activeEmployees = useMemo(() => {
    const data: any[] = Array.isArray(employees) ? employees : employees?.data ?? [];
    return data.filter((e: any) => e.Active_vs_Inactive === 'Active' || e.Active_vs_Inactive === undefined);
  }, [employees]);

  const employeeOptions = useMemo(() => {
    if (!activeEmployees.length) return [{ id: 'none', displayText: 'No employees found' }];
    return activeEmployees.map((e: any) => ({
      id: String(e.Id || e.GPID),
      displayText: `${e.First_Name} ${e.Last_Name}`.trim() || e.GPID,
    }));
  }, [activeEmployees]);

  // Manager display name for the selected employee — same raw-field fallback used in TabbedNavigation.tsx.
  const getManagerName = (employeeId: string): string => {
    const raw = activeEmployees.find((e: any) => String(e.Id || e.GPID) === employeeId);
    return raw?.Manager || raw?.Manager_Name || raw?.ManagerName || raw?.manager || '';
  };

  // Role name on the employee record itself (used to default the Role dropdown).
  const getEmployeeRoleName = (employeeId: string): string => {
    const raw = activeEmployees.find((e: any) => String(e.Id || e.GPID) === employeeId);
    return raw?.Role || raw?.ROLE_NAME || raw?.role || raw?.Job_Description || '';
  };

  // The dropdown's `id` is the numeric Id (falls back to GPID) — resolve the
  // actual GPID here so the backend always receives it, never the numeric Id.
  const getEmployeeGpid = (employeeId: string): string => {
    const raw = activeEmployees.find((e: any) => String(e.Id || e.GPID) === employeeId);
    return String(raw?.GPID ?? employeeId);
  };

  // Build role options from Redux (same as ObservationBegin / dropDownfilterOptions pattern)
  const roleOptions = useMemo(() => {
    if (!roles?.length) return [{ id: 'none', displayText: 'No roles found' }];
    return roles.map((r: any) => ({ id: String(r.ROLE_ID), displayText: r.ROLE_NAME }));
  }, [roles]);

  const [selectedEmployee, setSelectedEmployee] = useState<{ id: string; displayText: string } | null>(null);
  const [selectedRole, setSelectedRole] = useState<{ id: string; displayText: string } | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<{ id: string; displayText: string } | null>(
    ACTIVITY_TYPES[2], // default: Full Observation
  );
  const [isProceeding, setIsProceeding] = useState(false);

  // Pre-fill (and lock) the Employee field when opened from an Employee Profile page.
  // Resolves the dropdown's id (Id || GPID) from the preset GPID so the existing
  // employee-lookup helpers (manager/role/gpid) keep working unchanged.
  useEffect(() => {
    if (!visible || !presetEmployee) return;
    const raw = activeEmployees.find(
      (e: any) => String(e.GPID) === presetEmployee.employeeId || String(e.Id) === presetEmployee.employeeId,
    );
    const id = raw ? String(raw.Id || raw.GPID) : presetEmployee.employeeId;
    setSelectedEmployee({ id, displayText: presetEmployee.employeeName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, presetEmployee]);

  // Default the Role dropdown to the selected employee's own role whenever the employee changes.
  // Always resolve a fresh value here (instead of bailing out) so a previous employee's role
  // selection doesn't linger when the new employee has no match / no role on record.
  // When presetEmployee carries a role (profile page's current role), it takes priority as the
  // default — the user can still freely change the Role dropdown afterward.
  useEffect(() => {
    if (!selectedEmployee) {
      setSelectedRole(null);
      return;
    }
    const presetRoleName = presetEmployee?.role;
    const presetMatch = presetRoleName
      ? roleOptions.find(
          (r: { id: string; displayText: string }) => r.displayText.toLowerCase() === presetRoleName.toLowerCase()
        )
      : undefined;
    if (presetMatch) {
      setSelectedRole(presetMatch);
      return;
    }
    const employeeRoleName = getEmployeeRoleName(selectedEmployee.id);
    const match = employeeRoleName
      ? roleOptions.find(
          (r: { id: string; displayText: string }) => r.displayText.toLowerCase() === employeeRoleName.toLowerCase()
        )
      : undefined;
    setSelectedRole(match ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee]);

  const description = selectedActivity ? ACTIVITY_DESCRIPTIONS[selectedActivity.displayText] ?? '' : '';

  const handleProceed = async () => {
    if (!selectedEmployee || !selectedRole || !selectedActivity || isProceeding) return;
    setIsProceeding(true);
    try {
      await onProceed({
        employeeId: getEmployeeGpid(selectedEmployee.id),
        employee: selectedEmployee.displayText,
        role: selectedRole.displayText,
        managerName: getManagerName(selectedEmployee.id),
        activityType: selectedActivity.displayText,
      });
    } finally {
      // On success the caller hides the overlay (visible=false); on failure this
      // simply re-enables the button so the user can retry.
      setIsProceeding(false);
    }
  };

  const handleDismiss = () => {
    if (isProceeding) return;
    setSelectedEmployee(null);
    setSelectedRole(null);
    setSelectedActivity(ACTIVITY_TYPES[2]);
    onDismiss();
  };

  const isEmployeeLocked = !!presetEmployee;

  const canProceed = !!selectedEmployee && !!selectedRole && !!selectedActivity;

  return (
    <LHOverlay
      visible={visible}
      onDismiss={handleDismiss}
      title="Begin People activity"
      size="medium"
      hideFooter
      close={!isProceeding}
    >
      <View style={styles.container}>
        {/* ── Three dropdowns in a row ────────────────────────────────────── */}
        <View style={styles.grid}>
          <View style={styles.field}>
            <Text style={styles.label}>Employee</Text>
            <CrossPlatformDropdown
              childList={employeeOptions}
              label=""
              placeholder="Select employee"
              selection="single"
              selectedValue={selectedEmployee}
              setSelectedValue={(v: any) => setSelectedEmployee(Array.isArray(v) ? v[0] : v)}
              size="small"
              isClearable={!isEmployeeLocked}
              isDisabled={isEmployeeLocked}
            />
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Role</Text>
            <CrossPlatformDropdown
              childList={roleOptions}
              label=""
              placeholder="Select role"
              selection="single"
              selectedValue={selectedRole}
              setSelectedValue={(v: any) => setSelectedRole(Array.isArray(v) ? v[0] : v)}
              size="small"
              isClearable
            />
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Activity type</Text>
            <CrossPlatformDropdown
              childList={ACTIVITY_TYPES}
              label=""
              placeholder="Select activity"
              selection="single"
              selectedValue={selectedActivity}
              setSelectedValue={(v: any) => setSelectedActivity(Array.isArray(v) ? v[0] : v)}
              size="small"
              isClearable={false}
            />
          </View>
        </View>

        {/* ── Activity description banner ─────────────────────────────────── */}
        {!!description && (
          <View style={styles.descBanner}>
            <Text style={styles.descText}>{description}</Text>
          </View>
        )}

        {/* ── Footer buttons ─────────────────────────────────────────────── */}
        <View style={styles.footer}>
          <LhButton
            testID="begin-activity-cancel"
            text="Cancel"
            variant="secondary"
            size="small"
            onPress={handleDismiss}
            isLoading={false}
            disabled={isProceeding}
            style={styles.cancelButton}
          />
          <LhButton
            testID="begin-activity-proceed"
            text={isProceeding ? 'Starting…' : 'Proceed'}
            variant="primary"
            size="small"
            onPress={handleProceed}
            isLoading={isProceeding}
            disabled={!canProceed || isProceeding}
            style={styles.proceedButton}
          />
        </View>
      </View>
    </LHOverlay>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  grid: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    gap: 16,
    marginBottom: 16,
  },
  field: {
    flex: Platform.OS === 'web' ? 1 : undefined,
    minWidth: Platform.OS === 'web' ? 0 : undefined,
  },
  label: {
    fontFamily: FontFamily.medium,
    fontSize: 13,
    color: AdminColors.textPrimary,
    marginBottom: 6,
  },
  descBanner: {
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 20,
    backgroundColor: AdminColors.bgPage,
  },
  descText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.blue700,
    lineHeight: 18,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
  },
  cancelButton: {
    borderWidth: 0,
    width: Platform.OS === 'web' ? '15%' : '40%',
  },
  proceedButton: {
    width: Platform.OS === 'web' ? '15%' : '40%',
  },
});

export default BeginActivityOverlay;
